import { useCallback, useEffect, useState } from 'react';

import { type MessageFeedback } from '@verantos-dev/shared-components';

export interface UseFeedbackBarProps {
  messageId: string;
  feedback: MessageFeedback | null;
  onSubmitFeedback: (
    messageId: string,
    rating: 'thumbs_up' | 'thumbs_down',
    comment?: string
  ) => void;
  onDeleteFeedback: (messageId: string) => void;
  isVisible?: boolean;
  isSubmitting?: boolean;
  isDeleting?: boolean;
}

export interface UseFeedbackBarReturn {
  selectedRating: 'thumbs_up' | 'thumbs_down' | null;
  showCommentBox: boolean;
  comment: string;
  isEditing: boolean;
  hasSubmittedFeedback: boolean;
  shouldRender: boolean;
  isBusy: boolean;
  isSubmitDisabled: boolean;
  handleRatingClick: (rating: 'thumbs_up' | 'thumbs_down') => void;
  handleSubmit: () => void;
  handleEdit: () => void;
  handleCommentChange: (value: string) => void;
  handleCommentBlur: () => void;
}

export const useFeedbackBar = ({
  messageId,
  feedback,
  onSubmitFeedback,
  onDeleteFeedback,
  isVisible = true,
  isSubmitting = false,
  isDeleting = false,
}: UseFeedbackBarProps): UseFeedbackBarReturn => {
  const [selectedRating, setSelectedRating] = useState<'thumbs_up' | 'thumbs_down' | null>(
    feedback?.rating ?? null
  );
  const [showCommentBox, setShowCommentBox] = useState(false);
  const [comment, setComment] = useState(feedback?.comment ?? '');
  const [isEditing, setIsEditing] = useState(false);

  const isBusy = isSubmitting || isDeleting;

  useEffect(() => {
    if (!feedback || isDeleting) {
      return;
    }

    setSelectedRating(feedback.rating);
    if (!isEditing) {
      setComment(feedback.comment ?? '');
    }
  }, [feedback, isEditing, isDeleting]);

  const resetDraftState = useCallback(() => {
    setShowCommentBox(false);
    setIsEditing(false);
  }, []);

  const handleClearRating = useCallback(() => {
    if (isBusy) {
      return;
    }

    if (selectedRating) {
      onDeleteFeedback(messageId);
    }

    setSelectedRating(null);
    resetDraftState();
    setComment('');
  }, [isBusy, selectedRating, onDeleteFeedback, messageId, resetDraftState]);

  const trimmedComment = comment.trim();
  const trimmedSavedComment = (feedback?.comment ?? '').trim();
  const hasUnsubmittedChange = trimmedComment !== trimmedSavedComment;
  const isSubmitDisabled = isBusy || !hasUnsubmittedChange;

  const handleEdit = useCallback(() => {
    setIsEditing(true);
    setShowCommentBox(true);
  }, []);

  const handleRatingClick = useCallback(
    (rating: 'thumbs_up' | 'thumbs_down') => {
      if (selectedRating === rating) {
        handleClearRating();
        return;
      }

      if (rating === 'thumbs_up') {
        setSelectedRating(rating);
        onSubmitFeedback(messageId, rating);
        resetDraftState();
      } else {
        // Thumbs down: submit immediately and open comment box
        setSelectedRating(rating);
        onSubmitFeedback(messageId, rating);
        setIsEditing(true);
        setShowCommentBox(true);
        setComment('');
      }
    },
    [selectedRating, handleClearRating, messageId, onSubmitFeedback, resetDraftState]
  );

  const handleSubmit = useCallback(() => {
    if (isBusy) {
      return;
    }

    if (selectedRating) {
      let nextComment: string | undefined;
      if (trimmedComment.length > 0) {
        nextComment = trimmedComment;
      } else if (hasUnsubmittedChange && trimmedSavedComment.length > 0) {
        nextComment = '';
      }

      onSubmitFeedback(messageId, selectedRating, nextComment);
      setShowCommentBox(false);
      setIsEditing(false);
    }
  }, [
    isBusy,
    selectedRating,
    messageId,
    trimmedComment,
    trimmedSavedComment,
    hasUnsubmittedChange,
    onSubmitFeedback,
  ]);

  const handleCommentChange = useCallback((value: string) => {
    setComment(value);
  }, []);

  const handleCommentBlur = useCallback(() => {
    if (!hasUnsubmittedChange) {
      setShowCommentBox(false);
      setIsEditing(false);
    }
  }, [hasUnsubmittedChange]);

  const hasSubmittedFeedback = selectedRating !== null;
  const hasValidMessageId = Boolean(messageId && messageId.trim().length > 0);
  const shouldRender = isVisible && hasValidMessageId;

  return {
    selectedRating,
    showCommentBox,
    comment,
    isEditing,
    hasSubmittedFeedback,
    shouldRender,
    isBusy,
    isSubmitDisabled,
    handleRatingClick,
    handleSubmit,
    handleEdit,
    handleCommentChange,
    handleCommentBlur,
  };
};
